import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { CATEGORIES, CATEGORY_EXAMPLES, STYLE_ORDER } from "@/constants";
import { api } from "@/api";
import OrderDialog from "@/components/OrderDialog";
import PriceEstimator from "@/components/PriceEstimator";
import { useLightbox } from "@/components/LightboxProvider";
import { categoryWhatsappLink } from "@/lib/whatsapp";
import { ArrowLeft, ArrowUpRight, ZoomIn, MessageCircle, Star } from "lucide-react";

export default function CategoryPage() {
  const { key } = useParams();
  const { t } = useI18n();
  const [orderOpen, setOrderOpen] = useState(false);
  const [projects, setProjects] = useState([]);

  const cat = CATEGORIES.find((c) => c.key === key);
  const meta = cat ? t.categories[key] : null;
  const examples = CATEGORY_EXAMPLES[key] || [];

  const { unstyled, byStyle, isStyled } = useMemo(() => {
    const _unstyled = [];
    const _byStyle = {};
    let _has = false;
    examples.forEach((ex) => {
      if (ex.style) {
        _has = true;
        _byStyle[ex.style] = _byStyle[ex.style] || [];
        _byStyle[ex.style].push(ex);
      } else {
        _unstyled.push(ex);
      }
    });
    return { unstyled: _unstyled, byStyle: _byStyle, isStyled: _has };
  }, [examples]);

  useEffect(() => {
    if (!cat) return;
    api
      .get("/projects")
      .then((r) => setProjects(r.data.filter((p) => p.category === key)))
      .catch(() => setProjects([]));
  }, [cat, key]);

  if (!cat) return <Navigate to="/#catalog" replace />;

  return (
    <main className="min-h-screen pt-16" data-testid={`category-page-${key}`}>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 -z-10">
          <img src={cat.image} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-[#0A0A0A]" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
          <Link
            to="/#catalog"
            data-testid="back-to-catalog"
            className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-brand-blue-light transition-colors"
          >
            <ArrowLeft size={14} />
            {t.category_page.back}
          </Link>

          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] bg-brand-blue/15 border border-brand-blue/40 text-brand-blue-light px-2.5 py-1">
              #{cat.code}
            </span>
            <span className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-zinc-400">
              {meta.tag}
            </span>
          </div>

          <h1 className="font-heading mt-5 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
            {meta.name}
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-zinc-300 leading-relaxed">
            {meta.desc}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              data-testid={`category-request-btn-${key}`}
              onClick={() => setOrderOpen(true)}
              className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-light text-white px-6 py-3.5 uppercase tracking-widest text-xs font-bold border border-brand-blue-light/60 hover:shadow-[0_0_20px_rgba(0,85,255,0.55)] transition-all font-mono-accent"
            >
              {t.category_page.cta}
              <ArrowUpRight size={14} />
            </button>
            <a
              href={categoryWhatsappLink(meta.name)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`category-whatsapp-btn-${key}`}
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black px-6 py-3.5 uppercase tracking-widest text-xs font-bold border border-emerald-300/60 hover:shadow-[0_0_20px_rgba(16,185,129,0.55)] transition-all font-mono-accent"
            >
              <MessageCircle size={14} />
              {t.category_page.whatsapp_cta}
            </a>
          </div>
        </div>
      </section>

      {/* Announcement banner (e.g., In Stock — Jesus Keychains) */}
      {key === "in_stock" && meta.announcement_title && (
        <section
          data-testid="in-stock-announcement"
          className="relative overflow-hidden border-b border-white/5"
        >
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-brand-blue/20 via-amber-500/10 to-brand-blue/20" />
          <div className="absolute inset-0 -z-10 grid-bg opacity-20" />
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10 sm:py-14 flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
            <img
              src="https://customer-assets.emergentagent.com/job_print-3d-hub-3/artifacts/g1f3308r_1.avaible%20now_%20Jesus%20Keychains%20.jpg"
              alt="Jesus Keychains"
              className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-amber-300/40 shadow-[0_0_30px_rgba(251,191,36,0.25)]"
            />
            <div className="flex-1 text-center sm:text-left">
              <div className="font-mono-accent text-[11px] uppercase tracking-[0.28em] text-amber-300/90">
                ★ {meta.tag} ★
              </div>
              <h2 className="font-heading mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1]">
                <span className="text-white">{meta.announcement_title}</span>{" "}
                <span className="bg-gradient-to-r from-amber-300 to-brand-blue-light bg-clip-text text-transparent">
                  {meta.announcement_title_2}
                </span>
              </h2>
              <p className="mt-3 text-sm sm:text-base text-zinc-200">
                {meta.announcement_sub}
              </p>
              <button
                data-testid="in-stock-announcement-cta"
                onClick={() => setOrderOpen(true)}
                className="mt-5 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-5 py-3 uppercase tracking-widest text-xs font-bold border border-amber-200 hover:shadow-[0_0_20px_rgba(251,191,36,0.6)] transition-all font-mono-accent"
              >
                {t.category_page.cta}
                <ArrowUpRight size={14} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-2xl">
            <div className="font-mono-accent text-xs uppercase tracking-[0.25em] text-brand-blue-light">
              {t.category_page.gallery_eyebrow}
            </div>
            <h2 className="font-heading mt-3 text-3xl sm:text-4xl font-black tracking-tighter">
              {t.category_page.gallery_title}
            </h2>
            <p className="mt-4 text-zinc-400">{t.category_page.gallery_subtitle}</p>
          </div>

          {/* Unstyled cover tiles first */}
          {unstyled.length > 0 && (
            <div
              data-testid={`category-gallery-${key}`}
              className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
            >
              {unstyled.map((ex, i) => (
                <GalleryTile
                  key={`u-${i}`}
                  src={ex.url}
                  label={meta.name}
                  span={i === 0 ? "row-span-2 col-span-2" : ""}
                  testid={`category-gallery-tile-${key}-${i}`}
                />
              ))}
            </div>
          )}

          {/* Styled sections */}
          {isStyled && (
            <div className="mt-16 space-y-14">
              {STYLE_ORDER.filter((s) => byStyle[s]).map((s) => (
                <div key={s} data-testid={`category-style-section-${key}-${s}`}>
                  <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
                    <div>
                      <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-brand-blue-light">
                        / {meta.name} · {t.styles.label}
                      </div>
                      <h3 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter mt-2">
                        {t.styles[s]}
                      </h3>
                    </div>
                    <span
                      className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-zinc-500 italic"
                      data-testid={`illustrative-caption-${key}-${s}`}
                    >
                      * {t.illustrative}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {byStyle[s].map((ex, i) => (
                      <GalleryTile
                        key={`${s}-${i}`}
                        src={ex.url}
                        label={`${meta.name} — ${t.styles[s]}`}
                        testid={`category-style-tile-${key}-${s}-${i}`}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Price Estimator */}
      <PriceEstimator categoryKey={key} categoryName={meta.name} />

      {/* Completed (real deliveries) */}
      {projects.length > 0 && (
        <section className="py-16 border-t border-white/5 bg-black/40">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="font-mono-accent text-xs uppercase tracking-[0.25em] text-brand-blue-light">
              {t.category_page.completed_eyebrow}
            </div>
            <h3 className="font-heading mt-3 text-3xl font-black tracking-tighter">
              {t.category_page.completed_title}
            </h3>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {projects.map((p) => (
                <GalleryTile
                  key={p.id}
                  src={p.image_url}
                  label={p.title}
                  sub={p.description}
                  featured={p.featured}
                  featuredLabel={t.featured}
                  testid={`category-project-tile-${p.id}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <OrderDialog
        open={orderOpen}
        category={key}
        onClose={() => setOrderOpen(false)}
      />
    </main>
  );
}

function GalleryTile({ src, label, sub, span = "", testid, featured = false, featuredLabel = "Featured" }) {
  const { open } = useLightbox();
  return (
    <button
      type="button"
      onClick={() => open(src, label)}
      data-testid={testid}
      className={`group relative overflow-hidden border ${featured ? "border-amber-300/70 shadow-[0_0_22px_rgba(251,191,36,0.25)]" : "border-white/5"} hover:border-brand-blue/50 transition-colors text-left cursor-zoom-in ${span}`}
    >
      <img
        src={src}
        alt={label}
        loading="lazy"
        className="w-full h-60 md:h-72 object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
      {featured && (
        <div
          data-testid={`featured-badge-${testid}`}
          className="absolute top-3 left-3 inline-flex items-center gap-1 font-mono-accent text-[10px] font-bold uppercase tracking-[0.2em] text-black bg-amber-300 border border-amber-200 px-2 py-1"
        >
          <Star size={10} fill="currentColor" />
          {featuredLabel}
        </div>
      )}
      <div className="absolute top-3 right-3 bg-black/70 border border-white/10 group-hover:border-brand-blue/60 p-1.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
        <ZoomIn size={14} className="text-brand-blue-light" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="font-heading font-extrabold text-sm">{label}</div>
        {sub && <div className="text-xs text-zinc-300 mt-1 line-clamp-2">{sub}</div>}
      </div>
    </button>
  );
}
